import { preserveCanvasScroll } from "@/lib/cms-puck/preserve-canvas-scroll";
import { useSyncExternalStore } from "react";
import type { CatalogMediaKind } from "@/lib/cms-puck/media-utils";

/**
 * Store EXTERNE minimal pour le picker média du CMS avancé.
 *
 * Pourquoi ? Le picker (drawer) ne peut PAS vivre dans le rendu du champ Puck :
 * monter le formulaire d'upload désélectionne le bloc → Puck démonte le panneau
 * de champs → le drawer (et son state local) disparaissait. On déporte donc le
 * drawer dans un HOST monté UNE fois hors du panneau (cf. `MediaPickerHost`,
 * branché via `overrides.puck`). Le champ ne fait qu'OUVRIR le picker via ce
 * store ; le host (qui a accès à `usePuck`) écrit la valeur sur le bon bloc par
 * son id, même si le bloc a été désélectionné entre-temps.
 */
export type MediaPickerTab = "library" | "upload" | "catalog";

const ALL_CATALOG_MEDIA_KINDS: CatalogMediaKind[] = ["image", "video", "embed"];

type MediaPickerState = {
  open: boolean;
  /** Clé de prop à écrire sur le bloc (ex. "attachmentId" / "coverAttachmentId"). */
  fieldName: string | null;
  /**
   * Onglet actif. STOCKÉ ICI (pas en useState local) car le drawer peut être
   * re-monté par Puck au changement d'onglet → un state local serait réinitialisé.
   */
  tab: MediaPickerTab;
  /** Kinds autorisés dans l'onglet Catalogue (défaut : tous). */
  allowedCatalogKinds: CatalogMediaKind[];
};

let state: MediaPickerState = {
  open: false,
  fieldName: null,
  tab: "library",
  allowedCatalogKinds: ALL_CATALOG_MEDIA_KINDS,
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function openMediaPicker(
  fieldName: string,
  opts?: { allowedCatalogKinds?: CatalogMediaKind[] },
): void {
  preserveCanvasScroll();
  state = {
    open: true,
    fieldName,
    tab: "library",
    allowedCatalogKinds: opts?.allowedCatalogKinds ?? ALL_CATALOG_MEDIA_KINDS,
  };
  emit();
}

export function closeMediaPicker(): void {
  state = { ...state, open: false };
  emit();
}

export function setMediaPickerTab(tab: MediaPickerTab): void {
  state = { ...state, tab };
  emit();
}

export function useMediaPickerState(): MediaPickerState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}
